"use client";

import React from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useFormContext } from "react-hook-form";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import { Textarea } from "@/components/ui/input";
import { DottedGlowBackground } from "@/components/ui/dotted-glow-background";
import {
  LogoBeamBorder,
  useLogoPrimaryColor,
} from "@/components/ui/logo-beam";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Icon } from "@/components/ui/icon";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";

const MAX_LEN = 800;

const FIELDS = [
  {
    name: "employer.mission_vision",
    label: "Mission & Vision",
    hint: "What does your company exist to do?",
  },
  {
    name: "employer.funding_traction",
    label: "Funding & Traction",
    hint: "Funding raised, investors, growth, and key milestones.",
  },
  {
    name: "employer.culture_principles",
    label: "Culture & Values",
    hint: "What makes your company culture unique?",
  },
  {
    name: "employer.benefits",
    label: "Benefits",
    hint: "Perks, compensation highlights, and employee benefits.",
  },
] as const;

// Decided once per mount (the form view remounts after the research runs), so
// the copy doesn't flip while the user types.
function IntroCopy({ orgName }: { orgName: string }) {
  const { getValues } = useFormContext();
  const [foundSomething] = React.useState(() =>
    FIELDS.some((f) => String(getValues(f.name) ?? "").trim() !== ""),
  );

  if (!orgName) return null;
  return foundSomething ? (
    <>
      Here&rsquo;s what we found for {orgName}. Feel free to edit anything or
      leave fields blank.
    </>
  ) : (
    <>
      We couldn&rsquo;t find much about {orgName} yet, so add what you&rsquo;d
      like or leave fields blank.
    </>
  );
}

interface StepEmployerProfileProps {
  orgName: string;
  orgLogo?: string | null;
  enriching?: boolean;
}

function LogoWithBeam({
  orgLogo,
  orgName,
}: {
  orgLogo?: string | null;
  orgName: string;
}) {
  const beamColor = useLogoPrimaryColor(orgLogo);
  return (
    <div className="relative h-[76px] w-[76px]">
      <LogoBeamBorder color={beamColor} duration={3500} />
      <motion.div
        className="relative flex h-full w-full items-center justify-center overflow-hidden rounded-xl border border-contrast/10 shadow-sm"
        animate={{ scale: [1, 1.03, 1] }}
        transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
      >
        <Avatar className="h-full w-full rounded-xl">
          {orgLogo && (
            <AvatarImage
              src={orgLogo}
              alt={orgName}
              className="rounded-xl object-cover"
            />
          )}
          <AvatarFallback className="rounded-xl bg-surface-elevated text-2xl font-semibold text-contrast/30">
            {orgName?.[0]?.toUpperCase() || "?"}
          </AvatarFallback>
        </Avatar>
      </motion.div>
    </div>
  );
}

function ResearchAnimation({
  orgName,
  orgLogo,
}: {
  orgName: string;
  orgLogo?: string | null;
}) {
  return (
    <motion.div
      className="relative flex min-h-0 w-full flex-1 flex-col items-center justify-center overflow-hidden rounded-xl"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, scale: 0.97 }}
      transition={{ duration: 0.4 }}
    >
      <DottedGlowBackground
        className="!absolute inset-0 h-full w-full"
        gap={10}
        radius={1}
        darkColor="rgba(255,255,255,0.45)"
        darkGlowColor="rgba(59, 130, 246, 0.65)"
        color="rgba(0,0,0,0.25)"
        glowColor="rgba(59, 130, 246, 0.55)"
        opacity={0.5}
        speedMin={0.5}
        speedMax={1.6}
        speedScale={1.1}
      />

      <div className="relative z-10 flex flex-col items-center gap-5">
        <LogoWithBeam orgLogo={orgLogo} orgName={orgName} />

        <div className="flex flex-col items-center gap-1.5 text-center">
          <p className="text-base font-semibold text-heading tracking-tight">
            Researching {orgName || "your company"}
          </p>
          <p className="text-sm leading-5 text-heading/60">
            Building your employer profile
          </p>
        </div>
      </div>
    </motion.div>
  );
}

export function StepEmployerProfile({
  orgName,
  orgLogo,
  enriching = false,
}: StepEmployerProfileProps) {
  const { control } = useFormContext();
  const viewportRef = React.useRef<HTMLDivElement>(null);
  const [showScrollHint, setShowScrollHint] = React.useState(false);

  React.useEffect(() => {
    if (enriching) return;
    const el = viewportRef.current;
    if (!el) return;
    const update = () => {
      const { scrollTop, scrollHeight, clientHeight } = el;
      const scrollable = scrollHeight - clientHeight > 4;
      const atBottom = scrollTop + clientHeight >= scrollHeight - 8;
      setShowScrollHint(scrollable && !atBottom);
    };
    update();
    el.addEventListener("scroll", update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", update);
      ro.disconnect();
    };
  }, [enriching]);

  return (
    <AnimatePresence mode="wait">
      {enriching ? (
        <ResearchAnimation key="research" orgName={orgName} orgLogo={orgLogo} />
      ) : (
        <motion.div
          key="form"
          className="flex min-h-0 flex-1 flex-col overflow-hidden"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
        >
          <div className="relative -mr-2 flex-1 min-h-0">
            <ScrollArea className="h-full pr-2" viewportRef={viewportRef}>
              <p className="mb-4 text-sm leading-5 text-contrast/50">
                Your employer profile helps Arbi write better outreach copy,
                answer candidate questions, and personalize every interaction on
                the platform. <IntroCopy orgName={orgName} />
              </p>

              <p className="mb-5 text-[13px] leading-5 text-contrast/35">
                Sourcing for multiple companies? You can create additional
                employer profiles and update these anytime in your settings.
              </p>

              <div className="flex flex-col pb-6">
                {FIELDS.map((f) => (
                  <FormField
                    key={f.name}
                    control={control}
                    name={f.name as never}
                    render={({ field, fieldState }) => {
                      const value = (field.value as string | undefined) ?? "";
                      const error = fieldState.error?.message;
                      return (
                        <FormItem className="gap-1.5">
                          <div className="flex flex-col gap-0.5">
                            <FormLabel optional>{f.label}</FormLabel>
                            <FormDescription>{f.hint}</FormDescription>
                          </div>
                          <FormControl>
                            <Textarea
                              {...field}
                              value={value}
                              maxLength={MAX_LEN}
                              rows={4}
                              className={cn(
                                "h-[105px] resize-none",
                                error && "border-[#ff9d3b]/40 bg-[#ff9d3b]/5",
                              )}
                            />
                          </FormControl>
                          <div className="flex items-center justify-end text-[13px]">
                            <span className="text-contrast/60">
                              {value.length}/{MAX_LEN}
                            </span>
                          </div>
                        </FormItem>
                      );
                    }}
                  />
                ))}
              </div>
            </ScrollArea>
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-surface-elevated to-transparent" />
            <AnimatePresence>
              {showScrollHint && (
                <motion.div
                  key="scroll-hint"
                  className="pointer-events-none absolute inset-x-0 bottom-1.5 flex justify-center"
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.25, ease: "easeOut" }}
                >
                  <motion.span
                    className="flex items-center gap-1.5 rounded-full border border-contrast/10 bg-surface-elevated/80 px-2.5 py-1 text-[12px] font-medium text-contrast/55 shadow-sm backdrop-blur-sm"
                    animate={{ y: [0, 3, 0] }}
                    transition={{
                      duration: 1.6,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                  >
                    <Icon name="IconMouseScrollDown" size={14} />
                    Scroll for more
                  </motion.span>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
